import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  ArrowLeft, 
  ArrowRight, 
  Heart, 
  Crown, 
  Sparkles,
  FileText,
  CheckCircle,
  Palette,
  Upload
} from 'lucide-react';
import ElegantSidebar from '../../../shared/components/layout/ElegantSidebar';
import welcomeTemplate from '../../../img/welcometemplate.png';
import elegantTemplate from '../../../img/eleganttemplate.png';
import romanticTemplate from '../../../img/romantictemplate.png';
import '../styles/QRPaperCommon.css';
import { 
  DesignSelectLocationState as LocationState,
  RoomInfo,
  DesignTemplate
} from '../../../shared/types/qrPaper';

const QRPaperDesignSelect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [selectedDesign, setSelectedDesign] = useState<string>('');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [isLoadingRoom, setIsLoadingRoom] = useState(true);
  const [designType, setDesignType] = useState<'template' | 'original'>('template');
  const [originalDesignRequests, setOriginalDesignRequests] = useState<string>('');

  // リダイレクト処理とルーム情報取得
  useEffect(() => {
    if (!state?.roomid || !state?.qrUrl) {
      alert('ルーム情報が不正です。管理画面に戻ります。');
      navigate('/admin/room-create');
      return;
    }

    // ルーム情報を取得
    const fetchRoomInfo = async () => {
      try {
        setIsLoadingRoom(true);
        const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
        const response = await fetch(`${baseUrl}/api/room/${state.roomid}`);
        
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setRoomInfo({
            roomid: data.roomid,
            coupleName: data.coupleName || state.coupleName
          });
        } else {
          console.error('ルーム情報の取得に失敗:', data.message);
          setRoomInfo({
            roomid: state.roomid,
            coupleName: state.coupleName || state.roomid // stateのcoupleNameを優先、なければroomidをフォールバック
          });
        }
      } catch (error) {
        console.error('ルーム情報取得エラー:', error);
        setRoomInfo({
          roomid: state.roomid,
          coupleName: state.coupleName || state.roomid // stateのcoupleNameを優先、なければroomidをフォールバック
        });
      } finally {
        setIsLoadingRoom(false);
      }
    };

    fetchRoomInfo();
  }, [state, navigate]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 1024;
      setIsDesktop(newIsDesktop);
      
      if (newIsDesktop && !isDesktop) {
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDesktop]);

  const designTemplates: DesignTemplate[] = [
    {
      id: 'elegant',
      name: 'エレガント',
      description: '上品で洗練されたクラシックデザイン',
      previewClass: 'design-preview-welcome',
      icon: <Crown size={24} />,
      previewImage: elegantTemplate
    },
    {
      id: 'romantic',
      name: 'ロマンティック',
      description: '手を取り合った二人が移るシンプルなデザイン',
      previewClass: 'design-preview-romantic',
      icon: <Heart size={24} />,
      previewImage: romanticTemplate
    },
    {
      id: 'welcome',
      name: 'ウェルカム',
      description: 'カップルのイラストが描かれた温かみのあるデザイン',
      previewClass: 'design-preview-welcome',
      icon: <Heart size={24} />,
      previewImage: welcomeTemplate
    }
  ];

  const handleBack = () => {
    navigate('/admin/room-create');
  };

  const handleNext = () => {
    if (designType === 'template') {
      if (!selectedDesign) {
        alert('デザインテンプレートを選択してください。');
        return;
      }
    } else if (designType === 'original') {
      if (!originalDesignRequests.trim()) {
        alert('オリジナルデザインの要望を入力してください。');
        return;
      }
    }

    navigate('/admin/qr-paper/order', {
      state: {
        ...state,
        selectedDesign: designType === 'original' ? 'custom' : selectedDesign,
        designType,
        originalDesignRequests: designType === 'original' ? originalDesignRequests : undefined
      }
    });
  };

  const handleDesignTypeChange = (type: 'template' | 'original') => {
    setDesignType(type);
    setSelectedDesign(''); // 選択をリセット
    setOriginalDesignRequests(''); // オリジナルデザイン要望もリセット
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  if (!state?.roomid) {
    return null; // リダイレクト処理中
  }

  return (
    <div className="wedding-admin-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}
      
      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>
      
      <div className={`wedding-main-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <FileText size={24} />
          </button>
        )}

        {/* ヘッダーセクション */}
        <div className="wedding-header">
          <div className="wedding-header-decoration">
            <div className="crown-icon">
              <Crown size={32} />
            </div>
            <div className="sparkle-icon sparkle-1">
              <Sparkles size={20} />
            </div>
            <div className="sparkle-icon sparkle-2">
              <Sparkles size={16} />
            </div>
          </div>
          <h1 className="wedding-title">
            QRコード用紙作成
            <Heart className="title-heart" size={40} />
          </h1>
          <p className="wedding-subtitle">Design Selection - デザイン選択</p>
        </div>

        {/* メインコンテンツ */}
        <div className="qr-paper-content">
          {/* 進行状況インジケーター */}
          <div className="progress-indicator">
            <div className="progress-step active">
              <div className="step-number">1</div>
              <div className="step-label">デザイン選択</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step">
              <div className="step-number">2</div>
              <div className="step-label">情報入力</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step">
              <div className="step-number">3</div>
              <div className="step-label">同意確認</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step">
              <div className="step-number">4</div>
              <div className="step-label">最終確認</div>
            </div>
          </div>

          {/* ルーム情報表示 */}
          <div className="room-info-display">
            <div className="room-info-header">
              <FileText size={20} />
              {isLoadingRoom ? (
                <span>ルーム情報を読み込み中...</span>
              ) : (
                <span>
                  対象ルーム: {roomInfo?.coupleName || state.roomid}
                  {roomInfo?.coupleName && roomInfo.coupleName !== state.roomid && (
                    <span className="room-id-detail"> ({state.roomid})</span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* デザインタイプ選択 */}
          <div className="design-type-selector">
            <button 
              className={`design-type-button ${designType === 'template' ? 'active' : ''}`}
              onClick={() => handleDesignTypeChange('template')}
            >
              <FileText size={20} />
              デザインテンプレート
            </button>
            <button 
              className={`design-type-button ${designType === 'original' ? 'active' : ''}`}
              onClick={() => handleDesignTypeChange('original')}
            >
              <Palette size={20} />
              オリジナルデザイン
            </button>
          </div>

          {/* テンプレートデザイン選択カード */}
          {designType === 'template' && (
            <div className="wedding-card design-selection-card">
              <div className="card-header">
                <h2 className="card-title">デザインテンプレートを選択</h2>
                <p className="qr-paper-card-description">用意されたテンプレートから <br/>お好みのデザインを選択してください</p>
              </div>

              <div className="design-grid">
                {designTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`design-option ${selectedDesign === template.id ? 'selected' : ''}`}
                    onClick={() => setSelectedDesign(template.id)}
                  >
                    <div className="design-preview">
                        {template.previewImage ? (
                          // 画像がある場合は画像を表示
                          <div className="preview-image-container">
                            <img 
                              src={template.previewImage} 
                              alt={template.name}
                              className="preview-template-image"
                            />
                            {/* <div className="preview-qr-overlay">
                              <QRCodeCanvas
                                value={state.qrUrl}
                                size={60}
                                bgColor="#ffffff"
                                fgColor="#2d1b69"
                                level="M"
                              />
                            </div> */}
                          </div>
                        ) : (
                          // 画像がない場合は従来の表示
                          <>
                            <div className="preview-header">
                              <div className="preview-icon">
                                {template.icon}
                              </div>
                              <div className="preview-title">Photo Memorial</div>
                            </div>
                            
                            <div className="preview-qr">
                              <QRCodeCanvas
                                value={state.qrUrl}
                                size={80}
                                bgColor="#ffffff"
                                fgColor="#2d1b69"
                                level="M"
                              />
                            </div>
                            
                            <div className="preview-text">
                              Wedding Guest Access
                            </div>
                          </>
                        )}
                    </div>
                    
                    <div className="design-info">
                      <h3 className="design-name">{template.name}</h3>
                      <p className="design-description">{template.description}</p>
                    </div>
                    
                    {selectedDesign === template.id && (
                      <div className="design-selection-indicator">
                        <CheckCircle size={20} className="check-icon" />
                        <span className="selection-text">選択中</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* オリジナルデザイン選択カード */}
          {designType === 'original' && (
            <div className="wedding-card design-selection-card original-design-card">
              <div className="card-header">
                <h2 className="card-title">オリジナルデザインを選択</h2>
                <p className="qr-paper-card-description">お客様専用のオリジナルデザインを <br />ご希望の場合はこちらを選択してください</p>
              </div>

              <div className="original-design-content">
                <div className="original-design-option">
                  {/* <div 
                    className={`original-design-card ${selectedDesign === 'custom' ? 'selected' : ''}`}
                    onClick={() => setSelectedDesign('custom')}
                  > */}
                    {/* <div className="original-design-icon">
                      <Upload size={48} />
                    </div> */}
                    {/* <h3 className="original-design-title">オリジナルデザイン作成</h3>
                    <p className="original-design-description">
                      お客様のご要望に合わせて、完全オリジナルのデザインを作成いたします
                    </p>
                    <div className="original-design-features">
                      <div className="feature-item">✓ 完全カスタムデザイン</div>
                      <div className="feature-item">✓ お客様のお写真・ロゴ使用可能</div>
                      <div className="feature-item">✓ カラー・レイアウト自由</div>
                      <div className="feature-item">✓ 追加料金: ¥3,000</div>
                    </div> */}
                    
                    {/* {selectedDesign === 'custom' && (
                      <div className="design-selection-indicator">
                        <CheckCircle size={20} className="check-icon" />
                        <span className="selection-text">選択中</span>
                      </div>
                    )} */}
                  {/* </div> */}
                </div>

                {/* オリジナルデザイン要望入力フォーム */}
                {/* {selectedDesign === 'custom' && ( */}
                  <div className="original-design-requests">
                    <h4 className="requests-title">
                      <FileText size={18} />
                      デザインのご要望・詳細
                    </h4>
                    <textarea
                      className="requests-textarea"
                      placeholder="ご希望のデザインについて詳しくお聞かせください。&#13;&#10;例:&#13;&#10;- 使用したいカラー（ピンク、ゴールドなど）&#13;&#10;- テーマ・雰囲気（上品、可愛らしい、モダンなど）&#13;&#10;- 含めたい要素（お写真、ロゴ、特定の花柄など）&#13;&#10;- その他のご要望"
                      value={originalDesignRequests}
                      onChange={(e) => setOriginalDesignRequests(e.target.value)}
                      rows={8}
                      maxLength={1000}
                    />
                    <div className="requests-counter">
                      {originalDesignRequests.length}/1000文字
                    </div>
                  </div>
                {/* )} */}

                <div className="original-design-note">
                  <div className="note-header">
                    <FileText size={16} />
                    <span>ご注意事項</span>
                  </div>
                  <ul className="note-list">
                    <li>オリジナルデザインの場合、通常より2-3営業日長くお時間をいただきます</li>
                    <li>デザイン詳細は注文後に別途ご連絡いたします</li>
                    <li>デザイン案の確認・修正は最大2回まで可能です</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* アクションボタン */}
          <div className="action-buttons">
            <button 
              onClick={handleBack}
              className="wedding-button secondary-button back-button"
            >
              <ArrowLeft size={20} />
              戻る
            </button>
            
            <button 
              onClick={handleNext}
              className={`wedding-button primary-button ${
                (designType === 'template' && !selectedDesign) || 
                (designType === 'original' && !originalDesignRequests.trim()) ? 'disabled' : ''
              }`}
              disabled={
                (designType === 'template' && !selectedDesign) || 
                (designType === 'original' && !originalDesignRequests.trim())
              }
            >
              次へ
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="wedding-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Beautiful Memories, Elegant Design</p>
        </div>
      </div>
    </div>
  );
};

export default QRPaperDesignSelect;